// ITF-nahe Setzlisten-Logik (Draw-Größe, LK-Sortierung)

import { __asStr } from "./common.js";

export function parseTournamentLk(raw) {
  if (raw == null) return NaN;
  const normalized = __asStr(raw).trim().replace(",", ".");
  if (normalized === "") return NaN;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : NaN;
}

export function countPlayersWithLk(names, lkByPlayer) {
  if (!names || typeof names !== "object") return 0;
  const lk = lkByPlayer && typeof lkByPlayer === "object" ? lkByPlayer : {};
  let count = 0;
  for (const [key, name] of Object.entries(names)) {
    const n = __asStr(name).trim();
    if (!n || n === "(Optional)" || n === "(optional)") continue;
    if (Number.isFinite(parseTournamentLk(lk[String(key)]))) count += 1;
  }
  return count;
}

/**
 * Offizielle ITF-Regel: größte Zweierpotenz ≤ Draw/4 (ab 8 TN min. 2 Gesetzte).
 */
export function suggestOfficialSeedCount(drawSize) {
  const n = Math.max(0, Math.floor(Number(drawSize) || 0));
  if (n < 3) return 0;
  if (n < 8) return Math.min(2, n - 1);
  const quarter = Math.floor(n / 4);
  if (quarter < 2) return 2;
  let p = 1;
  while (p * 2 <= quarter) p *= 2;
  return Math.min(p, n - 1);
}

export function listAllowedSeedCounts(drawSize, rankedCount) {
  const n = Math.max(0, Math.floor(Number(drawSize) || 0));
  const ranked = Math.max(0, Math.floor(Number(rankedCount) || 0));
  if (n < 2 || ranked < 1) return [0];
  const maxOfficial = suggestOfficialSeedCount(n);
  const max = Math.min(ranked, maxOfficial, n - 1);
  const out = [0];
  let p = 1;
  while (p <= max) {
    out.push(p);
    p *= 2;
  }
  if (max >= 2 && !out.includes(2)) out.push(2);
  out.sort((a, b) => a - b);
  return [...new Set(out)];
}

export function resolveEffectiveSeeding({
  seedingMode,
  seedCount,
  entrantsCount,
  rankedCount,
}) {
  const ranked = Math.max(0, Math.floor(Number(rankedCount) || 0));
  if (ranked < 1) {
    return { seedingMode: "random", seedCount: 0 };
  }
  const mode = seedingMode === "seeded" ? "seeded" : "random";
  if (mode !== "seeded") {
    return { seedingMode: "random", seedCount: 0 };
  }
  const allowed = listAllowedSeedCounts(entrantsCount, ranked);
  const raw = Math.max(0, Math.floor(Number(seedCount) || 0));
  const clamped = allowed.includes(raw)
    ? raw
    : allowed[allowed.length - 1] || 0;
  return { seedingMode: "seeded", seedCount: clamped };
}

/**
 * Reihenfolge für Auslosung: Gesetzte nach LK, Rest zufällig (deterministisch per pid).
 */
export function orderPlayersForDraw(players, options = {}, shuffleFn) {
  const list = Array.isArray(players) ? players.slice() : [];
  const { seedingMode = "random", seedCount = 0 } = options;
  const shuffle =
    typeof shuffleFn === "function" ? shuffleFn : stableInterleaveShuffle;

  const withLk = list.map((p) => ({
    ...p,
    lk: parseTournamentLk(p.lk),
  }));

  const rankedCount = withLk.filter((p) => Number.isFinite(p.lk)).length;
  const effective = resolveEffectiveSeeding({
    seedingMode,
    seedCount,
    entrantsCount: list.length,
    rankedCount,
  });

  if (effective.seedingMode !== "seeded" || effective.seedCount <= 0) {
    return shuffle(withLk);
  }

  const ranked = withLk
    .filter((p) => Number.isFinite(p.lk))
    .sort(
      (a, b) => a.lk - b.lk || __asStr(a.pid).localeCompare(__asStr(b.pid)),
    );
  const unranked = withLk.filter((p) => !Number.isFinite(p.lk));

  const sc = Math.min(effective.seedCount, ranked.length);
  const seeds = ranked.slice(0, sc);
  const restRanked = ranked.slice(sc);
  const rest = shuffle([...restRanked, ...unranked]);
  return [...seeds, ...rest];
}

export function stableInterleaveShuffle(players) {
  const withKey = players.map((x) => ({
    x,
    k: hashPid(__asStr(x.pid || x.id || x.name || "")),
  }));
  withKey.sort((a, b) => a.k - b.k);
  const out = [];
  let i = 0;
  let j = withKey.length - 1;
  while (i <= j) {
    if (i === j) out.push(withKey[i].x);
    else {
      out.push(withKey[i].x);
      out.push(withKey[j].x);
    }
    i += 1;
    j -= 1;
  }
  return out;
}

function hashPid(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
